
import { Calendar, Users, Dumbbell, ChartBar, User, FileBarChart, Globe, Activity, BookOpen, BarChart } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  color?: string;
  image?: string;
}

const FeatureCard = ({ icon, title, description, color = "text-flame", image }: FeatureCardProps) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-slate-100/50 dark:border-gray-700/50 h-full flex flex-col feature-card">
    <div className={`mb-4 ${color} w-12 h-12 flex items-center justify-center rounded-lg bg-slate-50 dark:bg-gray-700`}>
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-2 text-black dark:text-white">{title}</h3>
    <p className="text-slate-gray-dark dark:text-gray-300 mb-4">{description}</p>
    {image && (
      <div className="mt-auto pt-4">
        <img 
          src={image} 
          alt={title} 
          className="rounded-lg w-full h-auto shadow-sm border border-slate-100 dark:border-gray-700" 
        />
      </div>
    )}
  </div>
);

export const Features = () => {
  const { t } = useLanguage();
  const { getPath } = useLanguagePath();
  
  return (
    <div id="features" className="py-24 bg-gradient-to-b from-white to-slate-50 dark:from-gray-900 dark:to-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-black dark:text-white mb-4">
            {t('landing.features.title')}
          </h2>
          <p className="text-xl text-slate-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t('landing.features.subtitle')}
          </p>
        </div>
        
        {/* Grid unificado com todas as funcionalidades */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            icon={<Calendar className="h-6 w-6" />}
            title={t('landing.features.scheduling.title')}
            description={t('landing.features.scheduling.description')}
            image="/lovable-uploads/4a125cc9-c826-4066-92b2-5539df46c5b7.png"
          />
          
          <FeatureCard
            icon={<Activity className="h-6 w-6" />}
            title={t('landing.features.physicalAssessment.title')}
            description={t('landing.features.physicalAssessment.description')}
            image="/lovable-uploads/e3590f1c-8458-4f52-aae0-fe7ca35ce7a2.png"
          />
          
          <FeatureCard
            icon={<BookOpen className="h-6 w-6" />}
            title={t('landing.features.exerciseLibrary.title')}
            description={t('landing.features.exerciseLibrary.description')}
            image="/lovable-uploads/4aea4340-682f-4355-a3e3-cfc01e7c4625.png"
          />
          
          <FeatureCard
            icon={<BarChart className="h-6 w-6" />}
            title={t('landing.features.dashboard.title')}
            description={t('landing.features.dashboard.description')}
            image="/lovable-uploads/48270ac5-4400-49fc-99f0-0bf594b48fcc.png"
          />
          
          <FeatureCard
            icon={<Dumbbell className="h-6 w-6" />}
            title={t('landing.features.aiTrainingPlans.title')}
            description={t('landing.features.aiTrainingPlans.description')}
            image="/lovable-uploads/11fd17f9-cc54-4c01-b005-4c57e29ebb08.png"
          />
          
          <FeatureCard
            icon={<Users className="h-6 w-6" />}
            title={t('landing.features.studentManagement.title')}
            description={t('landing.features.studentManagement.description')}
            image="/lovable-uploads/55633d45-4453-4c4d-b4e1-e98d6af9370c.png"
          />
          
          <FeatureCard
            icon={<User className="h-6 w-6" />}
            title={t('landing.features.publicProfile.title')}
            description={t('landing.features.publicProfile.description')}
            image="/lovable-uploads/70d72d56-6e3f-4842-9770-ca4c3e4e00b0.png"
          />
          
          <FeatureCard
            icon={<ChartBar className="h-6 w-6" />}
            title={t('landing.features.studentPortal.title')}
            description={t('landing.features.studentPortal.description')}
            image="/lovable-uploads/2dd79b84-cadf-4b9d-9c8f-8cebb5096d1a.png"
          />
          
          <FeatureCard
            icon={<FileBarChart className="h-6 w-6" />}
            title={t('landing.features.progressAnalysis.title')}
            description={t('landing.features.progressAnalysis.description')}
            image="/lovable-uploads/ece753df-ad8f-495d-b6e2-7dc8faf9bd58.png"
          />
        </div>
      </div>
    </div>
  );
};
